import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../state/auth-context';
import { consumeReturnUrl } from '../layouts/AuthenticatedLayout';

/**
 * Auth callback page. Handles the OAuth2 redirect from Cognito, exchanges
 * the authorization code for tokens via the AuthProvider, and redirects
 * to the preserved URL or /boards.
 *
 * Also handles Cognito error responses (e.g., error=... query params).
 *
 * Requirements: 9.2, 9.3
 */
export function AuthCallbackPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchangeStarted = React.useRef(false);

  useEffect(() => {
    // Check for Cognito error response first
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    if (errorParam) {
      const message = errorDescription
        ? `${errorParam}: ${decodeURIComponent(errorDescription.replace(/\+/g, ' '))}`
        : errorParam;
      setError(message);
      return;
    }

    const code = searchParams.get('code');

    if (!code) {
      setError('No authorization code received');
      return;
    }

    // Guard against double-execution (React Strict Mode or dependency changes)
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    handleCallback(code)
      .then(() => {
        const returnUrl = consumeReturnUrl() ?? '/boards';
        navigate(returnUrl, { replace: true });
      })
      .catch((err: Error) => {
        setError(err.message);
        exchangeStarted.current = false; // Allow retry on error
      });
  }, [searchParams, navigate, handleCallback]);

  if (error) {
    return (
      <main className="usa-section" role="main">
        <div className="grid-container">
          <div className="usa-alert usa-alert--error" role="alert">
            <div className="usa-alert__body">
              <h2 className="usa-alert__heading">Authentication Error</h2>
              <p className="usa-alert__text">{error}</p>
            </div>
          </div>
          <a href="/login" className="usa-button margin-top-3">
            Return to Login
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="usa-section" role="main" aria-busy="true">
      <div className="grid-container">
        <p>Completing authentication...</p>
      </div>
    </main>
  );
}
